import { __awaiter } from "tslib";
import { Device } from "@auvious/compatibility";
import { MediaDevices } from "./devices";
import { log, mergeConstraints, stopStream } from "./utils";
// MediaStreamTrack ended has other purpose
const stop = MediaStreamTrack.prototype.stop;
MediaStreamTrack.prototype.stop = function () {
    if (this.readyState === "live") {
        this.dispatchEvent(new Event("stopped"));
    }
    stop.call(this);
};
export class InputEffect {
    constructor() {
        this.name = "auvious.media-input";
        this.constraints = {};
        /** current active devices, null for none */
        this.activeDevices = {
            audioinput: null,
            videoinput: null,
        };
        /** previous non-null devices used */
        this.lastDevices = {
            audioinput: null,
            videoinput: null,
        };
        this.outputs = [];
        this.settingInput = false;
    }
    /** does it have an active input stream */
    get isActive() {
        var _a;
        return (_a = this.stream) === null || _a === void 0 ? void 0 : _a.active;
    }
    setInput(
    /// merge with existing tracks if some constraint kind is missing
    options, ...constraints) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            this.settingInput = true;
            const permissions = Object.assign({}, MediaDevices.permissions);
            // firefox concurrent mic limit
            const stopAudio = (Device.isMobile || Device.isFirefox) && !!constraints[0].audio;
            // mobiles do not allow concurrent cameras
            const stopVideo = Device.isMobile && !!constraints[0].video;
            const [stoppedAudio, stoppedVideo] = stopStream(this.stream, stopAudio, stopVideo);
            // stop only different tracks, if needed they would have been stopped previously
            if (this.previousStream) {
                stopStream(this.previousStream, !((_a = this.stream) === null || _a === void 0 ? void 0 : _a.getAudioTracks().includes(this.previousStream.getAudioTracks()[0])), !((_b = this.stream) === null || _b === void 0 ? void 0 : _b.getVideoTracks().includes(this.previousStream.getVideoTracks()[0])));
            }
            // user wants previous track on new output but we just stopped it
            if (!constraints[0].audio && stoppedAudio && options.merge) {
                constraints[0].audio = true;
                if (this.constraints.audio) {
                    constraints.push({ audio: this.constraints.audio });
                }
            }
            if (!constraints[0].video && stoppedVideo && options.merge) {
                constraints[0].video = true;
                if (this.constraints.video) {
                    constraints.push({ video: this.constraints.video });
                }
            }
            let mergedConstraints = mergeConstraints(constraints);
            let stream;
            try {
                stream = yield navigator.mediaDevices.getUserMedia(mergedConstraints);
            }
            finally {
                yield MediaDevices.refreshDevices().catch(() => { });
            }
            // preferred possibly updated
            const preferred = MediaDevices.preferredConstraints;
            const index = constraints.indexOf(preferred);
            // get stream again if preferred devices exist but were not selected
            if (index !== -1) {
                constraints[index] = preferred;
                mergedConstraints = mergeConstraints(constraints);
                const audioTrack = stream.getAudioTracks()[0];
                const videoTrack = stream.getVideoTracks()[0];
                const reset = 
                // audio was requested
                (audioTrack &&
                    // if permissions were already given, then preferred devices
                    //   1. are selected
                    //   2. are absent
                    //   3. exist but user explicitly picked other devices
                    permissions.audioinput !== MediaDevices.permissions.audioinput &&
                    // user has set preferred audio device
                    MediaDevices.preferred.audioinput &&
                    // current track does not originate from preferred device
                    audioTrack.label !== MediaDevices.preferred.audioinput.label &&
                    // actual device is plugged in
                    MediaDevices.getByLabel(MediaDevices.preferred.audioinput.label, "audioinput")) ||
                    (videoTrack &&
                        permissions.videoinput !== MediaDevices.permissions.videoinput &&
                        MediaDevices.preferred.videoinput &&
                        videoTrack.label !== MediaDevices.preferred.videoinput.label &&
                        MediaDevices.getByLabel(MediaDevices.preferred.videoinput.label, "videoinput"));
                if (reset) {
                    log("requesting preferred devices again");
                    audioTrack === null || audioTrack === void 0 ? void 0 : audioTrack.stop();
                    videoTrack === null || videoTrack === void 0 ? void 0 : videoTrack.stop();
                    stream = yield navigator.mediaDevices.getUserMedia(mergedConstraints);
                }
            }
            this.previousStream = this.stream;
            // should we merge with previous stream?
            let previous = (_c = this.previousStream) === null || _c === void 0 ? void 0 : _c.getAudioTracks()[0];
            if (options.merge && !mergedConstraints.audio && previous) {
                stream.addTrack(previous);
            }
            previous = (_d = this.previousStream) === null || _d === void 0 ? void 0 : _d.getVideoTracks()[0];
            if (options.merge && !mergedConstraints.video && previous) {
                stream.addTrack(previous);
            }
            this.constraints = Object.assign(Object.assign({}, this.constraints), mergedConstraints);
            this.updateMediaInfo(stream);
            this.setupStreamHandlers(stream);
            this.output(stream);
            this.settingInput = false;
            return this.stream;
        });
    }
    onOutput(callback) {
        this.outputs.push(callback);
    }
    output(stream) {
        this.previousStream = this.stream;
        this.stream = stream;
        this.outputs.forEach((callback) => callback(stream));
    }
    updateMediaInfo(stream) {
        this.activeDevices.audioinput = this.findTrackDevice(stream === null || stream === void 0 ? void 0 : stream.getAudioTracks()[0]);
        if (this.activeDevices.audioinput) {
            this.lastDevices.audioinput = this.activeDevices.audioinput;
        }
        const video = stream === null || stream === void 0 ? void 0 : stream.getVideoTracks()[0];
        this.facingMode = this.findFacingMode(video);
        this.activeDevices.videoinput = this.findTrackDevice(video);
        if (this.activeDevices.videoinput) {
            this.lastDevices.videoinput = this.activeDevices.videoinput;
        }
    }
    setupStreamHandlers(stream) {
        if (stream) {
            const handler = (event) => {
                var _a;
                const track = event.target;
                log("stream track ended", track);
                if (((_a = this.stream) === null || _a === void 0 ? void 0 : _a.getTracks().includes(track)) && !this.settingInput) {
                    this.activeDevices[track.kind === "audio" ? "audioinput" : "videoinput"] = null;
                    this.stream.removeTrack(track);
                    this.output(this.stream);
                }
            };
            stream.getTracks().forEach((track) => {
                track.addEventListener("ended", handler, { once: true });
                track.addEventListener("stopped", handler, { once: true });
            });
        }
        else {
            this.activeDevices.audioinput = null;
            this.activeDevices.videoinput = null;
        }
    }
    findTrackDevice(track) {
        if (!track) {
            return null;
        }
        const kind = track.kind === "audio" ? "audioinput" : "videoinput";
        const useSettings = () => {
            const id = track.getSettings().deviceId;
            return id ? MediaDevices.getById(id, kind) : null;
        };
        const useLabel = () => track.label ? MediaDevices.getByLabel(track.label, kind) : null;
        const useConstraints = () => {
            var _a;
            const id = this.constraints.audio instanceof Object
                ? this.constraints.audio.deviceId ||
                    (
                    // @ts-ignore
                    (_a = this.constraints.audio.deviceId) === null || _a === void 0 ? void 0 : _a.exact)
                : null;
            return id ? MediaDevices.getById(id, kind) : null;
        };
        return useSettings() || useLabel() || useConstraints();
    }
    findFacingMode(track) {
        if (!track) {
            return undefined;
        }
        if (track.getSettings().facingMode === "environment" ||
            /\bback\b/i.test(track.label) ||
            track.getConstraints().facingMode === "environment") {
            return "environment";
        }
        else {
            return "user";
        }
    }
}
